import { useUiTranslation } from 'lib/i18n/context';
import { useLayoutEffect, useRef, type Ref } from 'react';
import { useClaimReviewLayout } from './claim-review-layout-context';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { Button } from 'components/ui/button';
import { SidebarTrigger, useSidebar } from 'components/ui/sidebar/sidebar';

export const ClaimDetailsTrigger = ({
    expanded,
    onExpandedChange,
    controls,
    triggerRef,
}: {
    expanded: boolean;
    onExpandedChange: (expanded: boolean) => void;
    controls: string;
    triggerRef?: Ref<HTMLButtonElement>;
}) => {
    const { t } = useUiTranslation('common');
    return (
        <Button
            ref={triggerRef}
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 shrink-0 gap-1.5"
            aria-expanded={expanded}
            aria-controls={controls}
            onClick={() => onExpandedChange(!expanded)}
        >
            {expanded ? (
                <ChevronUp className="h-4 w-4" />
            ) : (
                <ChevronDown className="h-4 w-4" />
            )}
            <span className="sr-only sm:not-sr-only">
                {t(($) => $.claim_details)}
            </span>
        </Button>
    );
};

const NavigationTrigger = ({ navigationId }: { navigationId: string }) => {
    const { t } = useUiTranslation('common');

    const { open, openMobile, isMobile } = useSidebar();
    const expanded = isMobile ? openMobile : open;
    const ref = useRef<HTMLButtonElement>(null);
    useLayoutEffect(() => {
        if (
            !expanded &&
            document
                .getElementById(navigationId)
                ?.contains(document.activeElement)
        ) {
            ref.current?.focus({ preventScroll: true });
        }
    }, [expanded, navigationId]);
    return (
        <SidebarTrigger
            ref={ref}
            className="h-8 w-8 shrink-0"
            aria-label={
                expanded
                    ? t(($) => $.hide_navigation)
                    : t(($) => $.show_navigation)
            }
            aria-expanded={expanded}
            aria-controls={navigationId}
            title={t(($) => $.navigation)}
        />
    );
};

export const ClaimReviewNavigation = () => {
    const layout = useClaimReviewLayout();
    return layout ? (
        <NavigationTrigger navigationId={layout.navigationId} />
    ) : null;
};

// A claim may be loading or inaccessible, but the shell must remain operable.
export const ClaimReviewFallbackControls = () => {
    const layout = useClaimReviewLayout();
    if (!layout) return null;
    return (
        <div className="flex items-center justify-between gap-2 rounded-xl border bg-background p-2">
            <ClaimReviewNavigation />
            <ClaimDetailsTrigger
                expanded={layout.detailsExpanded}
                onExpandedChange={layout.setDetailsExpanded}
                controls={layout.appHeaderId}
            />
        </div>
    );
};
